import { useNavigate, Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { loginUrl } from "@/auth/loginUrl";
import { useAsyncData } from "@/hooks/useAsyncData";
import TicketCard from "@/components/TicketCard";
import ErrorState from "@/components/ErrorState";
import { Button } from "@/components/ui/button";
import { Ticket, Loader2, ArrowRight, Printer } from "lucide-react";

export default function MyTickets() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsyncData(() => (user ? api.orders.mine() : Promise.resolve([])), [user?.id]);
  const orders = data ?? [];

  if (status === "loading" || (user && loading)) return (
    <div className="flex justify-center items-center min-h-[70vh]">
      <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
    </div>
  );

  if (!user) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] px-4 text-center">
      <Ticket className="h-16 w-16 text-indigo-200 mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Sign in to view your tickets</h2>
      <p className="text-gray-500 mb-6">Your purchased tickets will appear here after signing in.</p>
      <Button onClick={() => navigate(loginUrl(createPageUrl("MyTickets")))} className="bg-indigo-600 hover:bg-indigo-700">
        Sign In
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 py-14 px-4 print:hidden">
        <div className="max-w-3xl mx-auto text-white">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">My Tickets</h1>
              <p className="text-indigo-200 mt-1">
                {orders.length > 0
                  ? `${orders.length} ticket purchase${orders.length !== 1 ? "s" : ""}`
                  : "No purchases yet"}
              </p>
            </div>
            {orders.length > 0 && (
              <Button
                onClick={() => window.print()}
                variant="secondary"
                className="gap-2 bg-white/15 hover:bg-white/25 text-white border-white/20 border"
              >
                <Printer className="h-4 w-4" /> Print All
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : orders.length > 0 ? (
          <div className="space-y-5">
            {orders.map((order) => <TicketCard key={order.id} order={order} />)}
          </div>
        ) : (
          <div className="text-center py-20">
            <Ticket className="h-20 w-20 text-gray-200 mx-auto mb-5" />
            <h3 className="text-xl font-bold text-gray-700 mb-2">No tickets yet</h3>
            <p className="text-gray-400 mb-6">Browse upcoming events and grab your first ticket!</p>
            <Link to={createPageUrl("Events")}>
              <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8">
                Browse Events <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
